import { ApiError, useUser } from '@berky/auth'
import {
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@berky/ui'
import { useDeferredValue, useState } from 'react'

import { useFeedback, useFeedbackAssignees, useFeedbackPlanning } from '../api'
import type { FeedbackAssignee, FeedbackEntry, FeedbackPriority } from '../types'

export const feedbackPriorityLabels: Record<FeedbackPriority, string> = {
  Low: 'Niedrig',
  Normal: 'Normal',
  High: 'Hoch',
}

export function formatTargetDate(value: string) {
  const [year, month, day] = value.split('-')
  return `${day}.${month}.${year}`
}

type PlanningSummary = Pick<FeedbackEntry, 'assigneeName' | 'priority' | 'targetDate'>

export function FeedbackPlanningSummary({ entry }: { entry: PlanningSummary }) {
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm" aria-label="Planung">
      <div>
        <dt className="text-xs text-muted-foreground">Verantwortlich</dt>
        <dd>{entry.assigneeName ?? 'Unzugewiesen'}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Priorität</dt>
        <dd>
          {entry.priority ? feedbackPriorityLabels[entry.priority] : 'Nicht festgelegt'}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Zieldatum</dt>
        <dd>
          {entry.targetDate ? formatTargetDate(entry.targetDate) : 'Nicht festgelegt'}
        </dd>
      </div>
    </dl>
  )
}

export function FeedbackAssignment({ entry }: { entry: FeedbackEntry }) {
  const [snapshot, setSnapshot] = useState<FeedbackEntry | null>(null)
  return (
    <section className="rounded-lg border p-3" aria-label="Verantwortung und Planung">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FeedbackPlanningSummary entry={entry} />
        <Button variant="outline" size="sm" onClick={() => setSnapshot(entry)}>
          Planung bearbeiten
        </Button>
      </div>
      <Dialog
        open={snapshot !== null}
        onOpenChange={(open) => {
          if (!open) setSnapshot(null)
        }}
      >
        {snapshot && (
          <PlanningEditor entry={snapshot} onClose={() => setSnapshot(null)} />
        )}
      </Dialog>
    </section>
  )
}

function PlanningEditor({
  entry,
  onClose,
}: {
  entry: FeedbackEntry
  onClose: () => void
}) {
  const currentUser = useUser()
  const [assignee, setAssignee] = useState({
    id: entry.assigneeId,
    name: entry.assigneeName,
  })
  const [priority, setPriority] = useState<FeedbackPriority | null>(entry.priority)
  const [targetDate, setTargetDate] = useState(entry.targetDate ?? '')
  const [version, setVersion] = useState(entry.planningVersion)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [searchRevision, setSearchRevision] = useState(0)
  const [reloadError, setReloadError] = useState(false)
  const deferredSearch = useDeferredValue(search)
  const people = useFeedbackAssignees(deferredSearch, page)
  const mutation = useFeedbackPlanning(entry.id)
  const detail = useFeedback(entry.id)
  const conflict = mutation.error instanceof ApiError && mutation.error.status === 409
  const options = people.data?.items ?? []

  function resetSearch() {
    setSearch('')
    setPage(1)
    setSearchRevision((revision) => revision + 1)
  }

  async function reload() {
    setReloadError(false)
    const fresh = await detail.refetch()
    if (fresh.isError || !fresh.data) {
      setReloadError(true)
      return
    }
    setAssignee({ id: fresh.data.assigneeId, name: fresh.data.assigneeName })
    setPriority(fresh.data.priority)
    setTargetDate(fresh.data.targetDate ?? '')
    setVersion(fresh.data.planningVersion)
    resetSearch()
    mutation.reset()
  }

  return (
    <DialogContent className="max-h-[90svh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Verantwortung und Planung</DialogTitle>
        <DialogDescription>
          Alle Angaben sind freiwillig. Priorität und Zieldatum sind unabhängig
          voneinander und von der Wiedervorlage.
        </DialogDescription>
      </DialogHeader>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          mutation.mutate(
            {
              assigneeId: assignee.id,
              priority,
              targetDate: targetDate || null,
              planningVersion: version,
            },
            {
              onSuccess: () => {
                toast.success('Planung gespeichert')
                onClose()
              },
            },
          )
        }}
        className="space-y-4"
      >
        <fieldset disabled={mutation.isPending || conflict} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assignee-search">Verantwortliche Person suchen</Label>
            <Combobox<FeedbackAssignee>
              key={searchRevision}
              inputId="assignee-search"
              maxLength={200}
              items={options}
              value={options.find((person) => person.id === assignee.id) ?? null}
              onChange={(person) => setAssignee(person)}
              onSearch={(value) => {
                setSearch(value)
                setPage(1)
              }}
              getKey={(person) => person.id}
              getLabel={(person) => person.name}
              placeholder="Name suchen"
              emptyText="Keine zuweisbare Person gefunden."
              loading={people.isFetching}
            />
            <p className="text-sm text-muted-foreground">
              Aktuell verantwortlich: {assignee.name ?? 'Unzugewiesen'}
            </p>
            {people.isPending && (
              <p className="text-sm text-muted-foreground" role="status">
                Personen werden geladen …
              </p>
            )}
            {people.isError && (
              <div role="alert" className="text-sm text-destructive">
                Die Personenauswahl konnte nicht geladen werden.{' '}
                <Button
                  type="button"
                  variant="link"
                  onClick={() => void people.refetch()}
                >
                  Erneut versuchen
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={assignee.id === currentUser.id}
                onClick={() => {
                  setAssignee({ id: currentUser.id, name: currentUser.name })
                  resetSearch()
                }}
              >
                Übernehmen
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!assignee.id}
                onClick={() => {
                  setAssignee({ id: null, name: null })
                  resetSearch()
                }}
              >
                Freigeben
              </Button>
            </div>
            {(page > 1 || (people.data?.total ?? 0) > 20) && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1 || people.isFetching}
                  onClick={() => setPage(page - 1)}
                >
                  Vorherige Personen
                </Button>
                <span className="text-xs text-muted-foreground">Seite {page}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={people.isFetching || page * 20 >= (people.data?.total ?? 0)}
                  onClick={() => setPage(page + 1)}
                >
                  Weitere Personen
                </Button>
              </div>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="planning-priority">Priorität (optional)</Label>
              <Select
                value={priority ?? 'none'}
                onValueChange={(value) =>
                  setPriority(value === 'none' ? null : (value as FeedbackPriority))
                }
              >
                <SelectTrigger id="planning-priority" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nicht festgelegt</SelectItem>
                  {Object.entries(feedbackPriorityLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="planning-date">Zieldatum (optional)</Label>
              <Input
                id="planning-date"
                type="date"
                min="0001-01-01"
                max="9999-12-31"
                value={targetDate}
                onChange={(event) => setTargetDate(event.target.value)}
              />
              {targetDate && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setTargetDate('')}
                >
                  Zieldatum entfernen
                </Button>
              )}
            </div>
          </div>
        </fieldset>
        {mutation.isError && (
          <div role="alert" className="space-y-2 text-sm text-destructive">
            <p>
              {mutation.error instanceof ApiError
                ? mutation.error.userMessage()
                : 'Die Planung konnte nicht gespeichert werden.'}
            </p>
            {conflict && (
              <>
                <p>
                  Laden Sie den aktuellen Stand. Ihre Eingaben in diesem Dialog werden
                  dabei ersetzt.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  disabled={detail.isFetching}
                  onClick={() => void reload()}
                >
                  Aktuellen Stand laden
                </Button>
              </>
            )}
          </div>
        )}
        {reloadError && (
          <p role="alert" className="text-sm text-destructive">
            Der aktuelle Stand konnte nicht geladen werden. Bitte versuchen Sie es erneut.
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={onClose}
          >
            Abbrechen
          </Button>
          <Button type="submit" disabled={mutation.isPending || conflict}>
            {mutation.isPending ? 'Speichert …' : 'Planung speichern'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
